/**
 * SC-010 — Admin Dashboard.
 *
 * Сводка: всего лидов, окна 24h/7d, воронка с конверсиями, источники,
 * активные разговоры, расход LLM (копейки), статусы outbox, re-engagement.
 *
 * Auto-refresh каждые 60 секунд: dashboard всегда показывает свежие
 * данные при просмотре, не требует ручного reload.
 */

import { useEffect, useState } from "react";

import { Alert, Card, Col, Progress, Row, Space, Spin, Statistic, Tag, Typography } from "antd";

import { api } from "../../lib/api";
import { LEAD_STATUS_COLOR, LEAD_STATUS_LABEL } from "../../lib/lead-status";
import type { AnalyticsSummary } from "../../types/analytics";
import type { LeadStatus } from "../../types/leads";

const REFRESH_INTERVAL_MS = 60_000;

function formatRubles(kopecks: number): string {
  const rubles = kopecks / 100;
  return rubles.toFixed(2) + " ₽";
}

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "medium" });
}

export const DashboardPage = (): JSX.Element => {
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async (): Promise<void> => {
      try {
        const response = await api.get<AnalyticsSummary>("/v1/admin/analytics/summary");
        if (!cancelled) {
          setData(response.data);
          setError(null);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Не удалось загрузить аналитику");
          setLoading(false);
        }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Ошибка загрузки" description={error} showIcon />
      </div>
    );
  }

  if (!data) {
    return <div />;
  }

  const funnelStatuses: LeadStatus[] = [
    "new",
    "qualified_a",
    "qualified_b",
    "qualified_c",
    "warmed",
    "enrolled",
    "rejected",
  ];

  const funnelCounts: Record<LeadStatus, number> = {
    new: data.funnel.new,
    qualified_a: data.funnel.qualified_a,
    qualified_b: data.funnel.qualified_b,
    qualified_c: data.funnel.qualified_c,
    warmed: data.funnel.warmed,
    enrolled: data.funnel.enrolled,
    rejected: data.funnel.rejected,
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Дашборд
        </Typography.Title>
        <Typography.Text type="secondary">
          Обновлено: {formatRelativeTime(data.generated_at)} · auto-refresh 60s
        </Typography.Text>
      </Space>

      {/* --- Топ-метрики: лиды */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Всего лидов" value={data.leads_total} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="За 24 часа"
              value={data.leads_24h}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="За 7 дней" value={data.leads_7d} />
          </Card>
        </Col>
      </Row>

      {/* --- Воронка */}
      <Card title="Воронка" style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          {funnelStatuses.map((status) => (
            <Col key={status} xs={12} sm={8} md={6} lg={4} xl={3}>
              <Card size="small">
                <Statistic
                  title={
                    <Tag color={LEAD_STATUS_COLOR[status]} style={{ marginBottom: 0 }}>
                      {LEAD_STATUS_LABEL[status]}
                    </Tag>
                  }
                  value={funnelCounts[status]}
                />
              </Card>
            </Col>
          ))}
        </Row>
        <Row gutter={16} style={{ marginTop: 24 }}>
          <Col xs={24} sm={12}>
            <Typography.Text strong>Конверсия в qualified (A/B/C):</Typography.Text>{" "}
            <Typography.Text>{data.funnel.conv_to_qualified_pct}%</Typography.Text>
            <Progress
              percent={data.funnel.conv_to_qualified_pct}
              status="active"
              strokeColor="#1890ff"
            />
          </Col>
          <Col xs={24} sm={12}>
            <Typography.Text strong>Конверсия в enrolled:</Typography.Text>{" "}
            <Typography.Text>{data.funnel.conv_to_enrolled_pct}%</Typography.Text>
            <Progress
              percent={data.funnel.conv_to_enrolled_pct}
              status="active"
              strokeColor="#52c41a"
            />
          </Col>
        </Row>
      </Card>

      {/* --- Источники, агенты */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={8}>
          <Card title="По источникам">
            {Object.entries(data.by_source).length === 0 ? (
              <Typography.Text type="secondary">нет данных</Typography.Text>
            ) : (
              Object.entries(data.by_source).map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{k}</span>
                  <strong>{v}</strong>
                </div>
              ))
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title="Активные разговоры">
            {Object.entries(data.by_current_agent).length === 0 ? (
              <Typography.Text type="secondary">нет данных</Typography.Text>
            ) : (
              Object.entries(data.by_current_agent).map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{k === "__finished__" ? "<завершено>" : k}</span>
                  <strong>{v}</strong>
                </div>
              ))
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title="Outbox">
            {Object.entries(data.outbox_by_status).length === 0 ? (
              <Typography.Text type="secondary">пусто</Typography.Text>
            ) : (
              Object.entries(data.outbox_by_status).map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{k}</span>
                  <strong>{v}</strong>
                </div>
              ))
            )}
          </Card>
        </Col>
      </Row>

      {/* --- LLM + Re-engagement */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Card title="Расходы на LLM">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="Сегодня" value={formatRubles(data.llm_cost_kopecks_today)} />
              </Col>
              <Col span={12}>
                <Statistic title="За 30 дней" value={formatRubles(data.llm_cost_kopecks_30d)} />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Re-engagement">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="В очереди nudge'ей" value={data.reengagement_pending} />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Вернулись (7д)"
                  value={data.reengagement_recovered_7d}
                  valueStyle={{ color: "#3f8600" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* --- Платежи + Команды --- */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Card title="Платежи (LK-005)">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Доход 30 дней"
                  value={formatRubles(data.payments.revenue_kopecks_30d)}
                  valueStyle={{ color: "#3f8600" }}
                />
              </Col>
              <Col span={12}>
                <Statistic title="Успешных" value={data.payments.by_status.succeeded ?? 0} />
              </Col>
            </Row>
            <Space wrap style={{ marginTop: 12 }}>
              {Object.entries(data.payments.by_status).map(([s, n]) => (
                <Tag key={s}>
                  {s}: {n}
                </Tag>
              ))}
              {Object.keys(data.payments.by_status).length === 0 && (
                <Typography.Text type="secondary">пока нет платежей</Typography.Text>
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Команды-«десятки»">
            <Row gutter={16}>
              <Col span={8}>
                <Statistic title="Команд всего" value={data.teams.total} />
              </Col>
              <Col span={8}>
                <Statistic title="С командой" value={data.teams.members_assigned} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Без команды (enrolled)"
                  value={data.teams.members_unassigned_enrolled}
                  valueStyle={{
                    color: data.teams.members_unassigned_enrolled > 0 ? "#cf1322" : "#3f8600",
                  }}
                />
              </Col>
            </Row>
            {data.teams.members_unassigned_enrolled > 0 && (
              <Typography.Text type="warning" style={{ display: "block", marginTop: 8 }}>
                {data.teams.members_unassigned_enrolled} ENROLLED-лидов не назначены — создай
                команду в Потоках, auto-assign подхватит.
              </Typography.Text>
            )}
          </Card>
        </Col>
      </Row>

      {/* --- График: лиды по дням */}
      <Card title="Новые лиды по дням (14)" style={{ marginTop: 16 }}>
        {data.daily_new_leads.length === 0 ? (
          <Typography.Text type="secondary">пока нет данных</Typography.Text>
        ) : (
          <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 100 }}>
            {data.daily_new_leads
              .slice()
              .reverse()
              .map((d) => {
                const max = Math.max(...data.daily_new_leads.map((x) => x.count));
                const height = max > 0 ? (d.count / max) * 100 : 0;
                return (
                  <div key={d.day} style={{ flex: 1, textAlign: "center" }}>
                    <div
                      title={`${d.day}: ${d.count}`}
                      style={{
                        background: "#1890ff",
                        height: `${Math.max(2, height)}px`,
                        marginBottom: 4,
                      }}
                    />
                    <Typography.Text style={{ fontSize: 10 }}>{d.day.slice(5)}</Typography.Text>
                    <br />
                    <Typography.Text strong style={{ fontSize: 11 }}>
                      {d.count}
                    </Typography.Text>
                  </div>
                );
              })}
          </div>
        )}
      </Card>
    </div>
  );
};
